/**
 * THE ONE SHAPE EVERY RESPONSE TAKES.
 *
 * Defined here, in the folder both sides import, because an envelope that is agreed
 * "by convention" drifts the first time somebody returns a bare object in a hurry.
 *
 * HTTP status carries transport meaning only — did the request arrive, was it routed. What
 * the domain thinks happened is in the body, because a phone on a hotel Wi-Fi gets captive
 * portals returning 200 with HTML and proxies rewriting 4xx, and neither is the service
 * talking.
 */
export type ApiOk<T> = { ok: true; data: T };
export type ApiErr = { ok: false; error: ApiErrorCode; message?: string; fields?: Record<string, string> };
export type ApiResult<T> = ApiOk<T> | ApiErr;

/**
 * Codes are stable strings, never numbers, and never prose: the app switches on them and
 * the translation layer turns them into a sentence in the user's language. Adding one is a
 * contract change both sides see in the same commit.
 */
export const API_ERRORS = {
  UNAUTHENTICATED: "UNAUTHENTICATED",
  FORBIDDEN: "FORBIDDEN",
  NOT_FOUND: "NOT_FOUND",
  VALIDATION_FAILED: "VALIDATION_FAILED",
  CONFLICT: "CONFLICT",
  RATE_LIMITED: "RATE_LIMITED",
  APP_TOO_OLD: "APP_TOO_OLD",
  SERVER_ERROR: "SERVER_ERROR",
  /** The request never reached the service — offline, DNS, TLS, captive portal. */
  NETWORK_UNREACHABLE: "NETWORK_UNREACHABLE",
} as const;

export type ApiErrorCode = (typeof API_ERRORS)[keyof typeof API_ERRORS];

export function ok<T>(data: T): ApiOk<T> {
  return { ok: true, data };
}

export function err(
  error: ApiErrorCode,
  extra?: { message?: string; fields?: Record<string, string> },
): ApiErr {
  return { ok: false, error, ...extra };
}
